import { useRef, useState } from 'react'
import { Alert, Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'
import { primary_color } from '../../../Helper/uiHelper'
import { getCookie } from '../../../Helper/cookieHelper'
import { useDispatch } from 'react-redux'
import { setNotification } from '../../../Redux/Reducers/notificationHandling'

/**
 * School Years → Codes: import overwrite via spreadsheet upload
 * (.csv / .xls / .xlsx) to automatic-codes/import-year-codes
 * Large files are queued server-side (avoids Cloudflare timeout / fake CORS errors).
 */
const ImportYearCodesButton = ({ disabled }) => {
  const dispatch = useDispatch()
  const fileRef = useRef(null)
  const pollRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [file, setFile] = useState(null)
  const [importErrors, setImportErrors] = useState([])
  const [importSummary, setImportSummary] = useState('')
  const [statusLine, setStatusLine] = useState('')

  const formatMessage = (message) => {
    if (!message) return 'Import failed'
    if (typeof message === 'string') return message
    if (typeof message === 'object') return Object.values(message).flat().join(' ')
    return 'Import failed'
  }

  const authHeaders = () => {
    const token = getCookie('AUTH_TOKEN')
    return {
      authorization: token && token !== 'undefined' ? `Bearer ${token}` : 'Bearer token',
      Accept: 'application/json',
    }
  }

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }

  const applyResult = (data, ok) => {
    const errList = Array.isArray(data?.errors) ? data.errors.filter(Boolean) : []
    if (ok && data?.success !== false) {
      dispatch(
        setNotification({
          type: 'success',
          message: data?.message || `Updated ${data?.updated || 0} lockers`,
        })
      )
      setOpen(false)
      reset()
      return
    }
    const summary = formatMessage(data?.message) || 'Import failed — no lockers were updated.'
    setImportSummary(summary)
    setImportErrors(errList)
    dispatch(
      setNotification({
        type: 'error',
        message:
          errList.length > 0
            ? `Import failed with ${errList.length} row error(s). See details in the import dialog.`
            : summary,
      })
    )
  }

  const pollImportStatus = (importId) => {
    setStatusLine('Import queued — processing in the background…')
    const started = Date.now()
    const maxMs = 45 * 60 * 1000

    stopPolling()
    pollRef.current = setInterval(async () => {
      if (Date.now() - started > maxMs) {
        stopPolling()
        setLoading(false)
        setStatusLine('')
        setImportSummary('Import is still running on the server, but this dialog timed out waiting. Check locker codes shortly, or try again with a smaller filtered export.')
        dispatch(setNotification({ type: 'error', message: 'Import is taking longer than expected' }))
        return
      }
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/automatic-codes/import-year-codes-status?import_id=${encodeURIComponent(importId)}`,
          { headers: authHeaders() }
        )
        const data = await response.json().catch(() => ({}))
        const status = data?.status
        if (status === 'queued' || status === 'processing') {
          setStatusLine(data?.message || (status === 'processing' ? 'Import is running…' : 'Import queued…'))
          return
        }
        stopPolling()
        setLoading(false)
        setStatusLine('')
        applyResult(data, response.ok && status === 'completed')
      } catch (e) {
        // Keep polling through transient network blips
      }
    }, 3000)
  }

  const reset = () => {
    stopPolling()
    setFile(null)
    setImportErrors([])
    setImportSummary('')
    setStatusLine('')
    if (fileRef.current) fileRef.current.value = ''
  }

  const closeModal = () => {
    if (loading) return
    setOpen(false)
    reset()
  }

  const run = () => {
    if (!file) {
      dispatch(setNotification({ type: 'error', message: 'Please choose a spreadsheet (.csv, .xls, or .xlsx)' }))
      return
    }
    const ext = (file.name.split('.').pop() || '').toLowerCase()
    if (!['csv', 'xls', 'xlsx'].includes(ext)) {
      dispatch(setNotification({ type: 'error', message: 'File must be .csv, .xls, or .xlsx' }))
      return
    }

    setLoading(true)
    setImportErrors([])
    setImportSummary('')
    setStatusLine('Uploading…')
    const formData = new FormData()
    formData.append('file', file)

    fetch(`${process.env.REACT_APP_API_URL}/automatic-codes/import-year-codes`, {
      method: 'POST',
      body: formData,
      headers: authHeaders(),
    })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}))
        if (response.ok && data?.queued && data?.import_id) {
          pollImportStatus(data.import_id)
          return
        }
        setLoading(false)
        setStatusLine('')
        applyResult(data, response.ok && data?.success !== false)
      })
      .catch(() => {
        setLoading(false)
        setStatusLine('')
        setImportSummary(
          'Import failed — could not reach the server. For very large year exports this used to look like a CORS error; try again after the latest fix, or import a smaller filtered export.'
        )
        setImportErrors([])
        dispatch(setNotification({ type: 'error', message: 'Import failed' }))
      })
  }

  return (
    <>
      <Button
        className='subtitle1 mb-2'
        disabled={disabled}
        style={{ background: primary_color, border: 'none' }}
        onClick={() => setOpen(true)}>
        Import Codes
      </Button>
      <Modal isOpen={open} toggle={closeModal} centered size={importErrors.length ? 'lg' : undefined} style={{ boxShadow: 'none' }}>
        <ModalHeader toggle={closeModal}>Import Locker Codes</ModalHeader>
        <ModalBody>
          <div className='subtitle1 mb-3' style={{ lineHeight: 1.5 }}>
            <p className='mb-2'>Search for the lockers you want to update.</p>
            <p className='mb-2'>Click Export Data to download the spreadsheet template.</p>
            <p className='mb-2'>
              Or download a{' '}
              <a
                href={`${process.env.PUBLIC_URL || ''}/templates/locker-codes-import-sample.xlsx`}
                download='locker-codes-import-sample.xlsx'>
                sample template
              </a>{' '}
              (headers and example rows only — replace with real locker_id values before importing).
            </p>
            <p className='mb-2'>Make the required changes in the spreadsheet.</p>
            <p className='mb-2'>Import the updated spreadsheet.</p>
            <p className='mb-2'>
              <strong>Important:</strong> Do not change locker_id — this is used to identify the correct locker.
            </p>
            <p className='mb-2'>School, Block and Locker Number are for reference only.</p>
            <p className='mb-2'>Manual lockers: change the Code.</p>
            <p className='mb-2'>Automatic lockers: change the Lock ID and Sequence.</p>
            <p className='mb-3'>
              Be careful: importing the spreadsheet will directly overwrite the existing locker data. Large files run in the background so the request does not time out.
            </p>
          </div>
          <input
            ref={fileRef}
            type='file'
            accept='.csv,.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv'
            className='form-control-file'
            disabled={loading}
            onChange={(e) => {
              setFile(e.target.files?.[0] || null)
              setImportErrors([])
              setImportSummary('')
            }}
          />
          {file ? <div className='mt-2 subtitle1 text-muted'>Selected: {file.name}</div> : null}
          {statusLine ? <div className='mt-2 subtitle1 text-info'>{statusLine}</div> : null}

          {(importSummary || importErrors.length > 0) && (
            <Alert color='danger' className='mt-3 mb-0'>
              <div className='font-weight-bold mb-2'>
                {importSummary || `Import failed with ${importErrors.length} row error(s). No lockers were updated.`}
              </div>
              {importErrors.length > 0 ? (
                <>
                  <div className='mb-1 subtitle1'>Fix these rows and import again:</div>
                  <div
                    style={{
                      maxHeight: 280,
                      overflowY: 'auto',
                      background: '#fff',
                      border: '1px solid #f5c2c7',
                      borderRadius: 4,
                      padding: '8px 12px',
                    }}>
                    <ol className='mb-0 pl-3' style={{ lineHeight: 1.45 }}>
                      {importErrors.map((err, idx) => (
                        <li key={`${idx}-${err}`} className='subtitle1 mb-1'>
                          {err}
                        </li>
                      ))}
                    </ol>
                  </div>
                </>
              ) : null}
            </Alert>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color='secondary' disabled={loading} onClick={closeModal}>
            Cancel
          </Button>
          <Button color='primary' style={{ background: primary_color, border: 'none' }} disabled={loading || !file} onClick={run}>
            {loading ? 'Importing…' : 'Import'}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default ImportYearCodesButton
